import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { EmotionLevel } from './EmotionSelector';
import { getEmotionPercentage } from '@/utils/emotionHelpers';
import { cn } from "@/lib/utils";
import { useIsMobile } from '@/hooks/use-mobile';

interface QuoteBubbleProps {
  quote: string;
  emotion: EmotionLevel;
  onQuoteChange: (quote: string) => void;
}

const QuoteBubble: React.FC<QuoteBubbleProps> = ({ 
  quote, 
  emotion, 
  onQuoteChange 
}) => {
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [quoteText, setQuoteText] = useState(quote || '');
  const quoteRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    setQuoteText(quote || '');
  }, [quote]);
  
  const handleQuoteSubmit = () => {
    onQuoteChange(quoteText);
    setIsEditingQuote(false);
  };

  const handleClearQuote = () => {
    setQuoteText('');
  };
  
  const handleCancelQuote = () => {
    setQuoteText(quote || '');
    setIsEditingQuote(false);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditingQuote) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleQuoteSubmit();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelQuote();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditingQuote, quoteText, quote, onQuoteChange]);
  
  // Scroll into view when editing starts
  useEffect(() => {
    if (isEditingQuote && quoteRef.current && isMobile) {
      // Use a short timeout to ensure the DOM has updated
      setTimeout(() => {
        quoteRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
      }, 100);
    }
  }, [isEditingQuote, isMobile]);
  
  // Adjust the bubble arrow to point to the face icon's position
  const bubbleArrowStyle = {
    top: `${getEmotionPercentage(emotion) * 0.8}%`  // Adjusted to align with face icon
  };
  
  return (
    <div ref={quoteRef}>
      {isEditingQuote ? (
        <div className="relative">
          <div className="relative">
            <Textarea
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
              placeholder="E.g., 'Where do I click next?' or 'This is exactly what I needed!'"
              className={cn(
                "text-xs whitespace-pre-wrap overflow-wrap-break-word", 
                isMobile ? "min-h-[60px]" : "min-h-[80px]"
              )}
              rows={isMobile ? 2 : 3}
              autoFocus
            />
            {quoteText && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 absolute top-1 right-1 p-0"
                onClick={handleClearQuote}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Clear</span>
              </Button>
            )}
          </div>
          
          <div className="flex justify-end mt-1 gap-1">
            <Button 
              size="sm" 
              variant="ghost" 
              className={cn("text-xs", isMobile && "h-6 px-2")}
              onClick={handleCancelQuote}
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              className={cn("text-xs", isMobile && "h-6 px-2")}
              onClick={handleQuoteSubmit}
              disabled={!quoteText.trim()}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg text-xs relative cursor-pointer min-h-[80px] flex items-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          onClick={() => setIsEditingQuote(true)}
          style={{ position: 'relative' }}
        >
          {quote ? (
            <p className={cn(
              "text-xs whitespace-pre-wrap",
              quote ? "text-foreground" : "text-muted-foreground"
            )}>
              {quote}
            </p>
          ) : (
            <div className="flex items-center text-muted-foreground gap-1 w-full justify-center italic">
              <MessageCircle className="h-3 w-3" />
              <span>Add a quote of what the user says or thinks</span>
            </div>
          )}
          <div 
            className="absolute w-0 h-0 right-[-10px]"
            style={{
              ...bubbleArrowStyle,
              borderTop: '10px solid transparent',
              borderBottom: '10px solid transparent',
              borderLeft: '10px solid var(--quote-bubble-color, #f3f4f6)'
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default QuoteBubble;
